/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import { useCallback } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import AvatarGroup from '@atlaskit/avatar-group';
import AkFlag, { type FlagProps } from '@atlaskit/flag';
import { Box, xcss } from '@atlaskit/primitives';

import { type Invitees } from '../../common/types';
import { withLocalIntl } from '../../common/utils/i18n/intl-provider';

import { messages } from './messages';

export type Props = {
	id: FlagProps['id'];
	invitees: Invitees;
	onDismiss?: (id: FlagProps['id']) => void;
	onOptOut?: () => void;
	onSubmit: () => Promise<void>;
};

export const InvitePromptOnPagePublishFlagNoIntl = ({
	invitees,
	onSubmit,
	onOptOut,
	onDismiss,
	...props
}: Props) => {
	const handleOnSubmit = useCallback(async () => {
		await onSubmit();
	}, [onSubmit]);

	const { formatMessage } = useIntl();
	const optOutEnabled = onOptOut !== undefined;

	return (
		<AkFlag
			{...props}
			testId="invite-prompt-on-page-publish"
			icon={
				<Box xcss={avatarsContainerStyles}>
					<AvatarGroup
						data={[
							{
								name: invitees[0].name,
								src: invitees[0].avatar,
								appearance: 'circle',
								size: 'small',
							},
						]}
						size={'small'}
					/>
				</Box>
			}
			onDismissed={onDismiss}
			title={
				<Box as="p" xcss={subtleColorStyles}>
					{formatMessage(messages.title, {
						displayName: invitees[0].name.split(' ')[0],
					})}
				</Box>
			}
			description={
				<Box xcss={mainContentContainerStyles} as="p">
					{formatMessage(messages.description, {
						displayName: invitees[0].name,
					})}
				</Box>
			}
			actions={[
				{
					content: formatMessage(messages.addToConfluenceButton),
					onClick: () => {
						handleOnSubmit();
					},
				},
				...(optOutEnabled
					? [
							{
								content: formatMessage(messages.dismiss),
								onClick: () => {
									onOptOut();
								},
							},
						]
					: []),
			]}
		/>
	);
};

export const InvitePromptOnPagePublishFlag = withLocalIntl(InvitePromptOnPagePublishFlagNoIntl);

const avatarsContainerStyles = xcss({
	marginLeft: 'space.negative.025', // Aligns avatars with the rest of the text
	marginBottom: 'space.050',
});

const mainContentContainerStyles = xcss({
	display: '-webkit-box',
	lineClamp: '3',
	boxOrient: 'vertical',
	overflow: 'hidden',
	margin: 'space.0',
	color: 'color.text.subtle',
});

const subtleColorStyles = xcss({
	color: 'color.text.subtle',
});
